import { ChangeDetectorRef, Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import {
	VehicleInsuranceListDto,
	VehicleInsuranceRecordDto,
	VehicleInsurancesAppOptionsDto,
	VehicleInsurancesFilters,
	VehicleInsurancesStatsDto,
	VehicleInsuranceSearchDto,
	VehicleInsuranceRecordCategoryEnum,
	VehicleInsuranceStatusEnum,
	vehicleInsuranceValidators,
	vehicleInsuranceStatusToColor,
	CreateVehicleInsuranceDto,
	UpdateVehicleInsuranceDto,
	ModulesEnum,
	TagRecordCategoryEnum,
} from '@splitsecondconnect/ultramarine-shared';

import { RecordTabsComponent } from '../../../../layout/components/record-tabs/record-tabs.component';
import { TabContentDirective } from '../../../../layout/components/record-tabs/tab-content.directive';
import { BaseRecordComponent } from '../../../../shared/base-classes/base-record.component';
import { HeaderDataModel } from '../../../../shared/components/headers/record-header/record-header.model';
import { ModuleSelectorComponent } from '../../../../shared/components/module-selector/module-selector.component';
import { MoreDetailsComponent } from '../../../../shared/components/more-details/more-details.component';
import { NoActivityComponent } from '../../../../shared/components/no-activity.component';
import { AnnotationsSectionComponent } from '../../../../shared/components/annotations-section/annotations-section.component';
import { ADMINISTRATION_RECORD_TABS } from '../../../../shared/enums/administration-record-tabs';
import { dateCast } from '../../../../shared/functions/sectionCasters.function';
import {
	mapBaseDtoToBaseModel,
	mapBaseModelToBaseDto,
} from '../../../../shared/mapper/base.mapper';
import {
	mapFilesDtoToModel,
	mapFilesModelToDto,
} from '../../../../shared/mapper/files.mapper';
import {
	mapTagDtoToModel,
	mapTagModelToDto,
} from '../../../../shared/mapper/tags.mapper';
import { MODULE_SELECTOR_STRATEGY_TOKEN } from '../../../../shared/tokens/module-selector.token';
import { VehicleSectionComponent } from '../../vehicles/sections/vehicle-section/vehicle-section.component';
import { VehicleInsurancesListItemMenuComponent } from '../components/vehicle-insurances-list-item-menu/vehicle-insurances-list-item-menu.component';
import { VehicleInsuranceModuleSelectorStrategy } from '../config/vehicle-insurance-module-selector-strategy';
import { VehicleInsurancesRecordModel } from '../models/vehicle-insurances-record.model';
import { VehicleInsurancesService } from '../vehicle-insurances.service';
import { VehicleInsurancesDetailsComponent } from './components/vehicle-insurances-details/vehicle-insurances-details.component';
import { VehicleInsurancesSummaryComponent } from './components/vehicle-insurances-summary/vehicle-insurances-summary.component';
import { TAGS_RECORD_CATEGORY_TOKEN } from '../../../../shared/tokens/tags-record-category.token';
import { CommentsSectionComponent } from 'app/shared/components/comments-section/comments-section.component';
import { StepperComponent } from '../../../../shared/components/stepper/stepper.component';
import { SectionMetadataDirective } from '../../../../shared/directives/section-metadata.directive';
import { ContextInfoComponent } from '../../../../shared/components/context-info/context-info.component';
import { ErrorsBoxComponent } from '../../../../shared/components/errors-box/errors-box.component';
import { UltraPrimaryButtonComponent } from '../../../../shared/components/ultramarine-ui/ultra-primary/ultra-primary.component';
import { CommentsCardComponent } from '../../../../shared/components/comments-card/comments-card.component';
import { RecordHeaderComponent } from '../../../../shared/components/headers/record-header/record-header.component';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { RecordFooterComponent } from '../../../../shared/components/footers/record-footer/record-footer.component';

@Component({
	selector: 'app-vehicle-insurances-record',
	templateUrl: 'vehicle-insurances-record.component.html',
	host: {
		'[class]': 'modalClass()',
	},
	providers: [
		{
			provide: MODULE_SELECTOR_STRATEGY_TOKEN,
			useClass: VehicleInsuranceModuleSelectorStrategy,
		},
		{
			provide: TAGS_RECORD_CATEGORY_TOKEN,
			useValue: TagRecordCategoryEnum.VEHICLE_INSURANCE,
		},
	],
	imports: [
		VehicleInsurancesDetailsComponent,
		VehicleSectionComponent,
		VehicleInsurancesListItemMenuComponent,
		AnnotationsSectionComponent,
		RecordTabsComponent,
		RecordHeaderComponent,
		RecordFooterComponent,
		AsyncPipe,
		VehicleInsurancesSummaryComponent,
		TabContentDirective,
		IonicModule,
		ModuleSelectorComponent,
		MoreDetailsComponent,
		NoActivityComponent,
		CommentsSectionComponent,
		StepperComponent,
		SectionMetadataDirective,
		ContextInfoComponent,
		ErrorsBoxComponent,
		UltraPrimaryButtonComponent,
		CommentsCardComponent,
		NgTemplateOutlet,
	],
})
export class VehicleInsurancesRecordComponent extends BaseRecordComponent<
	VehicleInsuranceRecordDto,
	CreateVehicleInsuranceDto,
	UpdateVehicleInsuranceDto,
	VehicleInsuranceListDto,
	VehicleInsurancesStatsDto,
	VehicleInsurancesAppOptionsDto,
	VehicleInsurancesFilters,
	VehicleInsurancesRecordModel,
	VehicleInsuranceStatusEnum,
	VehicleInsuranceSearchDto
> {
	constructor(
		private _vehicleInsurancesService: VehicleInsurancesService,
		private _activatedRoute: ActivatedRoute,
		private _router: Router,
		private _changeDetectorRef: ChangeDetectorRef,
	) {
		super(
			_vehicleInsurancesService,
			_router,
			_activatedRoute,
			_changeDetectorRef,
		);
		this.module = ModulesEnum.VEHICLE_INSURANCES;
	}

	recordTabs = ADMINISTRATION_RECORD_TABS;

	recordCategories = VehicleInsuranceRecordCategoryEnum;

	protected _isRecordFormValid(): boolean {
		const validationResult = vehicleInsuranceValidators.canCreateOrUpdate(
			this._mapModelToDto(this.recordData()),
		);
		this.errorMessages.next(validationResult.errors);
		return validationResult.isValid;
	}

	protected _updateHeaderData(
		record: VehicleInsurancesRecordModel,
	): HeaderDataModel<VehicleInsuranceStatusEnum> {
		return {
			...this._updateHeaderDataBase(record),
			title: record.code,
			status: record.status,

			color: vehicleInsuranceStatusToColor(record.status),
		};
	}

	protected _mapDtoToModel(
		dto: VehicleInsuranceRecordDto,
	): VehicleInsurancesRecordModel {
		return {
			...mapBaseDtoToBaseModel(dto),
			code: dto.code ?? null,
			notes: dto.notes ?? null,
			externalReference: dto.externalReference ?? null,
			startsAt: dateCast(dto.startsAt),
			endsAt: dateCast(dto.endsAt),
			status: dto.status ?? null,
			vehicleId: dto.vehicleId ?? null,
			taxRateType: dto.taxRateType,
			taxRate: dto.taxRate ?? null,
			isTaxIncluded: dto.isTaxIncluded ?? null,
			amount: dto.amount ?? null,
			totalNet: dto.totalNet ?? null,
			totalTax: dto.totalTax ?? null,
			totalGross: dto.totalGross ?? null,
			premiumPeriod: dto.premiumPeriod,
			excessAmount: dto.excessAmount ?? null,
			insuredValue: dto.insuredValue ?? null,
			totalLossPercentage: dto.totalLossPercentage ?? null,
			computedTotalLossAmountReceivable:
				dto.computedTotalLossAmountReceivable ?? null,
			coverageType: dto.coverageType ?? null,
			...mapTagDtoToModel(dto.tags),
			recordCategory: dto.recordCategory ?? null,
			...mapFilesDtoToModel(dto.files),
		};
	}

	protected _mapModelToDto(
		model: VehicleInsurancesRecordModel,
	): VehicleInsuranceRecordDto {
		return {
			...mapBaseModelToBaseDto(model),
			code: model.code ?? null,
			notes: model.notes ?? null,
			externalReference: model.externalReference ?? null,
			startsAt: dateCast(model.startsAt) ?? null,
			endsAt: dateCast(model.endsAt) ?? null,
			vehicleId: model.vehicleId ?? null,

			taxRateType: model.taxRateType ?? null,
			taxRate: model.taxRate ?? null,
			isTaxIncluded: model.isTaxIncluded ?? null,
			amount: model.amount ?? null,
			totalNet: model.totalNet ?? null,
			totalTax: model.totalTax ?? null,
			totalGross: model.totalGross ?? null,
			premiumPeriod: model.premiumPeriod ?? null,
			excessAmount: model.excessAmount ?? null,
			insuredValue: model.insuredValue ?? null,
			totalLossPercentage: model.totalLossPercentage ?? null,
			computedTotalLossAmountReceivable:
				model.computedTotalLossAmountReceivable ?? null,
			coverageType: model.coverageType ?? null,
			...mapTagModelToDto(model.tags),
			recordCategory: model.recordCategory ?? null,
			...mapFilesModelToDto(model.files),
			status: model.status ?? null,
		};
	}

	protected _mapModelToCreateDto(
		model: VehicleInsurancesRecordModel,
	): CreateVehicleInsuranceDto {
		return {
			...mapBaseModelToBaseDto(model),
			code: model.code ?? null,
			notes: model.notes ?? null,
			externalReference: model.externalReference ?? null,
			startsAt: dateCast(model.startsAt) ?? null,
			endsAt: dateCast(model.endsAt) ?? null,
			vehicleId: model.vehicleId ?? null,

			taxRateType: model.taxRateType ?? null,
			taxRate: model.taxRate ?? null,
			isTaxIncluded: model.isTaxIncluded ?? null,
			amount: model.amount ?? null,
			totalNet: model.totalNet ?? null,
			totalTax: model.totalTax ?? null,
			totalGross: model.totalGross ?? null,
			premiumPeriod: model.premiumPeriod ?? null,
			excessAmount: model.excessAmount ?? null,
			insuredValue: model.insuredValue ?? null,
			totalLossPercentage: model.totalLossPercentage ?? null,
			computedTotalLossAmountReceivable:
				model.computedTotalLossAmountReceivable ?? null,
			coverageType: model.coverageType ?? null,
			...mapTagModelToDto(model.tags),
			recordCategory: model.recordCategory ?? null,
			...mapFilesModelToDto(model.files),
		};
	}

	protected _mapModelToUpdateDto(
		model: VehicleInsurancesRecordModel,
	): UpdateVehicleInsuranceDto {
		return {
			...mapBaseModelToBaseDto(model),
			code: model.code ?? null,
			notes: model.notes ?? null,
			externalReference: model.externalReference ?? null,
			startsAt: dateCast(model.startsAt) ?? null,
			endsAt: dateCast(model.endsAt) ?? null,
			vehicleId: model.vehicleId ?? null,

			taxRateType: model.taxRateType ?? null,
			taxRate: model.taxRate ?? null,
			isTaxIncluded: model.isTaxIncluded ?? null,
			amount: model.amount ?? null,
			totalNet: model.totalNet ?? null,
			totalTax: model.totalTax ?? null,
			totalGross: model.totalGross ?? null,
			premiumPeriod: model.premiumPeriod ?? null,
			excessAmount: model.excessAmount ?? null,
			insuredValue: model.insuredValue ?? null,
			totalLossPercentage: model.totalLossPercentage ?? null,
			computedTotalLossAmountReceivable:
				model.computedTotalLossAmountReceivable ?? null,
			coverageType: model.coverageType ?? null,
			...mapTagModelToDto(model.tags),
			recordCategory: model.recordCategory ?? null,
			...mapFilesModelToDto(model.files),
		};
	}
}
